import { useCallback } from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import { formatPlainTemporal } from '../../../helpers/timeZone'
import type { ValidationError } from '../types'
import {
  isTimeInRange,
  clampTime,
  createPlainTime,
  formatTime,
} from '../utils/temporalHelpers'

interface UseValueCommitProps {
  /** Current time value */
  value: Temporal.PlainTime | Temporal.ZonedDateTime | null | undefined
  /** Precision level for time input */
  granularity: 'hour' | 'minute'
  /** Minimum allowed time */
  min: Temporal.PlainTime | undefined
  /** Maximum allowed time */
  max: Temporal.PlainTime | undefined
  /** Time format preference */
  resolvedFormat: '12' | '24'
  /** Current input field value */
  inputValue: string
  /** Callback when time value changes */
  onChange?: (time: Temporal.PlainTime | null) => void
  /** Callback when validation fails */
  onValidationError?: (error: ValidationError) => void
  /** Updates internal time state */
  onSetInternalValue: (value: Temporal.PlainTime | null) => void
  /** Updates input field value */
  onSetInput: (value: string, resetNavigation: boolean) => void
  /** Closes the suggestion menu */
  onCloseMenu: () => void
  /** Sets or clears validation error */
  onSetValidationError: (error: ValidationError | null) => void
}

/**
 * Handles value commitment and validation for SmartTimeInput
 *
 * Manages the final step of committing a time value, including granularity adjustment,
 * range validation, formatting for display, and triggering appropriate callbacks.
 *
 * @param props.value - Current time value
 * @param props.granularity - Precision level for time input
 * @param props.min - Minimum allowed time
 * @param props.max - Maximum allowed time
 * @param props.resolvedFormat - Time format preference
 * @param props.inputValue - Current input field value
 * @param props.onChange - Callback when time value changes
 * @param props.onValidationError - Callback when validation fails
 * @param props.onSetInternalValue - Updates internal time state
 * @param props.onSetInput - Updates input field value
 * @param props.onCloseMenu - Closes the suggestion menu
 * @param props.onSetValidationError - Sets or clears validation error
 *
 * @returns Object with commitValue, formatForDisplay, and createRangeError functions
 */
export const useValueCommit = ({
  value,
  granularity,
  min,
  max,
  resolvedFormat,
  inputValue,
  onChange,
  onValidationError,
  onSetInternalValue,
  onSetInput,
  onCloseMenu,
  onSetValidationError,
}: UseValueCommitProps) => {
  const createRangeError = useCallback(
    (originalInput: string): ValidationError => {
      const formatBoundary = (
        value: Temporal.PlainTime | undefined,
        fallback: { twelveHour: string; twentyFourHour: string },
      ) =>
        value
          ? formatTime(value, resolvedFormat)
          : resolvedFormat === '12'
            ? fallback.twelveHour
            : fallback.twentyFourHour

      return {
        type: 'out-of-range',
        message: `Time must be between ${formatBoundary(min, {
          twelveHour: '12:00 AM',
          twentyFourHour: '00:00',
        })} and ${formatBoundary(max, {
          twelveHour: '11:59 PM',
          twentyFourHour: '23:59',
        })}`,
        originalInput,
      }
    },
    [max, min, resolvedFormat],
  )

  // Formats time for display in en-US (pinned for parser round-trip)
  const formatForDisplay = useCallback(
    (time: Temporal.PlainTime): string => {
      if (granularity === 'hour') {
        const options: Intl.DateTimeFormatOptions = {
          hour: resolvedFormat === '12' ? 'numeric' : '2-digit',
        }
        if (resolvedFormat === '12') {
          options.hour12 = true
        } else {
          options.hour12 = false
          options.hourCycle = 'h23'
        }
        return formatPlainTemporal(time, 'en-US', options)
      }
      return formatTime(time, resolvedFormat)
    },
    [granularity, resolvedFormat],
  )

  const commitValue = useCallback(
    (timeValue: Temporal.PlainTime | null) => {
      let finalValue = timeValue

      if (finalValue) {
        if (granularity === 'hour') {
          finalValue = createPlainTime(finalValue.hour, 0)
        } else if (granularity === 'minute') {
          finalValue = createPlainTime(finalValue.hour, finalValue.minute)
        }
      }

      if (finalValue) {
        // Clamp value to valid range first
        const originalValue = finalValue
        finalValue = clampTime(finalValue, min, max)

        // Surface validation error if value was out of range, but continue with clamped value
        if (!isTimeInRange(originalValue, min, max)) {
          const error = createRangeError(inputValue)
          onSetValidationError(error)
          onValidationError?.(error)
        }
      }

      if (value === undefined) {
        onSetInternalValue(finalValue)
      }

      onChange?.(finalValue)

      if (finalValue) {
        onSetInput(formatForDisplay(finalValue), true)
      } else {
        onSetInput('', true)
      }

      onCloseMenu()
      onSetValidationError(null)
    },
    [
      min,
      max,
      inputValue,
      value,
      onChange,
      onValidationError,
      granularity,
      formatForDisplay,
      createRangeError,
      onSetInternalValue,
      onSetInput,
      onCloseMenu,
      onSetValidationError,
    ],
  )

  return {
    commitValue,
    formatForDisplay,
    createRangeError,
  }
}
